// One-message-at-a-time scheduling for the scrolling-notification ticker,
// mirroring Unity's CampaignManager: each message is shown up to its repeat
// count (repeat_instance), and after every showing it goes on cooldown for its
// repeat interval (repeat_interval). Messages rotate by Campaign ID first, then
// each campaign's sortOrder; once every message has hit its repeat count the
// loop resets. Pure + side-effect-free so
// it can be unit tested without the DOM/GSAP; the component owns the clock and
// the in-memory runtime map.

export type SchedulerMessage = {
  campaignId: number;
  id: number;
  /** Max times to show before this message drops out until the loop resets. */
  repeatInstance: number;
  /** Cooldown after each showing, in milliseconds. */
  repeatIntervalMs: number;
  sortOrder: number;
  /**
   * Stable position in the admin/server order (0-based index across the
   * flattened campaigns → messages). Used as the ordering tie-break instead of
   * `id`: admin can reorder messages without their ids changing, so `id` order
   * ≠ admin order. Sequencing on the server-delivered order keeps first-display
   * order matching Admin (and Unity).
   */
  seq: number;
};

export type MessageRuntime = {
  displayCount: number;
  /** Epoch ms before which the message may not be shown again. */
  nextAvailableTime: number;
};

export const messageKey = (campaignId: number, id: number): string => `${campaignId}:${id}`;

/**
 * Re-selection identity guard for the marquee.
 *
 * The ticker re-picks the message to show on several triggers unrelated to the
 * message actually changing — cross-instance runtime sync (the lobby + table
 * tickers share one schedule), a redux campaign refresh (which hands the pool a
 * new array reference), and the cooldown wake timer. Each re-pick returns a
 * FRESH object, even for the message already on screen. If the component swaps
 * `current` to that fresh object, its scroll animation (keyed on `current`)
 * tears down and restarts from the right edge — the message visibly "jumps back
 * to the start" mid-scroll.
 *
 * This keeps the EXISTING object reference whenever the newly-picked message is
 * the same one (same campaign + id), so the scroll keeps running; only a
 * genuinely different message (or a first/cleared transition) swaps it.
 */
export const preserveCurrent = <T extends SchedulerMessage>(
  prev: T | null,
  next: T | null
): T | null =>
  prev && next && prev.campaignId === next.campaignId && prev.id === next.id ? prev : next;

export const getRuntime = (
  states: Map<string, MessageRuntime>,
  campaignId: number,
  id: number
): MessageRuntime => {
  const key = messageKey(campaignId, id);
  let state = states.get(key);
  if (!state) {
    state = { displayCount: 0, nextAvailableTime: 0 };
    states.set(key, state);
  }
  return state;
};

// A configured repeat count is at least 1 (0/blank/negative → show once).
export const maxDisplays = (message: SchedulerMessage): number =>
  Math.max(1, Math.floor(Number(message.repeatInstance) || 1));

export const isAvailable = (
  message: SchedulerMessage,
  states: Map<string, MessageRuntime>,
  now: number
): boolean => {
  const state = getRuntime(states, message.campaignId, message.id);
  return state.displayCount < maxDisplays(message) && now >= state.nextAvailableTime;
};

/**
 * The message to show right now, ordered by:
 *   1. campaignId  — lower Campaign ID first, so a campaign's messages play as a
 *      GROUP before the next campaign starts (never interleaved).
 *   2. sortOrder   — the admin's per-campaign message order within that campaign.
 *   3. soonest-available (cooldown), then
 *   4. seq         — the admin/server delivery order.
 * Returns null when nothing is available this instant.
 *
 * campaignId is the PRIMARY key because `sort_order` is scoped per campaign
 * (each campaign's messages start at 0). Ordering globally by sort_order alone
 * interleaved campaigns — e.g. campaign A's 2nd message (order 1) displayed
 * AFTER campaign B/C's 1st messages (order 0). Grouping by campaign first makes
 * first-display order follow the admin's Campaign ID → message order.
 *
 * The final tie-break is `seq` (position in the admin-configured order), NOT
 * `id` — admin reordering keeps ids fixed, so an id tie-break rendered messages
 * out of the configured sequence on the first display cycle.
 */
export const pickNextMessage = <T extends SchedulerMessage>(
  messages: T[],
  states: Map<string, MessageRuntime>,
  now: number
): T | null => {
  let best: T | null = null;
  let bestState: MessageRuntime | null = null;

  for (const message of messages) {
    if (!isAvailable(message, states, now)) continue;
    const state = getRuntime(states, message.campaignId, message.id);
    const sameCampaign = !!best && message.campaignId === best.campaignId;
    const sameOrder = sameCampaign && !!best && message.sortOrder === best.sortOrder;
    if (
      !best ||
      !bestState ||
      message.campaignId < best.campaignId ||
      (sameCampaign && message.sortOrder < best.sortOrder) ||
      (sameOrder && state.nextAvailableTime < bestState.nextAvailableTime) ||
      (sameOrder &&
        state.nextAvailableTime === bestState.nextAvailableTime &&
        message.seq < best.seq)
    ) {
      best = message;
      bestState = state;
    }
  }

  return best;
};

/**
 * Record that `shown` was just displayed: bump its count and start its cooldown.
 * When every message has reached its repeat count, reset the whole loop (counts
 * to 0, each back on its cooldown) so the campaign repeats — matching Unity.
 * Mutates `states` in place.
 */
export const recordDisplay = (
  messages: SchedulerMessage[],
  states: Map<string, MessageRuntime>,
  shown: SchedulerMessage,
  now: number
): void => {
  const state = getRuntime(states, shown.campaignId, shown.id);
  state.displayCount += 1;
  state.nextAvailableTime = now + Math.max(0, shown.repeatIntervalMs);

  const allMaxed = messages.every(
    (message) =>
      getRuntime(states, message.campaignId, message.id).displayCount >= maxDisplays(message)
  );
  if (allMaxed) {
    for (const message of messages) {
      const messageState = getRuntime(states, message.campaignId, message.id);
      messageState.displayCount = 0;
      messageState.nextAvailableTime = now + Math.max(0, message.repeatIntervalMs);
    }
  }
};

/**
 * Milliseconds until the soonest message becomes available (0 if one is
 * available now, Infinity if there are none). Drives the wait between messages
 * when everything is on cooldown.
 */
export const timeUntilNext = (
  messages: SchedulerMessage[],
  states: Map<string, MessageRuntime>,
  now: number
): number => {
  let min = Number.POSITIVE_INFINITY;
  for (const message of messages) {
    const state = getRuntime(states, message.campaignId, message.id);
    if (state.displayCount >= maxDisplays(message)) continue;
    const wait = Math.max(0, state.nextAvailableTime - now);
    if (wait < min) min = wait;
  }
  return min;
};
